import { z } from "zod"

import {
  stringNoNumbersOrSpecialCharacters,
  dateString,
  ssn4or9,
} from "./form-fields"
import { STATE_CODES } from "./loan-product"
import { zipCodePattern, mosaicPhoneNumberFormat } from "./regex"

const PHONE_VALUES = [`HOME`, `MOBILE`] as const

const EMPLOYMENT_STATUS = [
  `EMPLOYED`,
  `NOT_EMPLOYED`,
  `OTHER`,
  `RETIRED`,
] as const

const CITIZENSHIP_STATUS = [
  `LAWFUL_PERMANENT_RESIDENT_ALIEN`,
  `OTHER`,
  `US_CITIZEN`,
] as const

const OWNERSHIP_VALUE = [
  `NOT_OWNED`,
  `OWNED_FREE_AND_CLEAR`,
  `OWNED_WITH_MORTGAGE`,
] as const

const OCCUPANCY_VALUE = [`INVESTMENT`, `OTHER`, `PRIMARY`, `SECONDARY`] as const

export const goodleapCoApplicantSchema = z.object({
  coApplicant_firstName: stringNoNumbersOrSpecialCharacters,
  coApplicant_lastName: stringNoNumbersOrSpecialCharacters,
  coApplicant_dob: dateString,
  coApplicant_ssn: ssn4or9,
  coApplicant_street: z.string().min(1, `required`),
  coApplicant_street2: z.string(),
  coApplicant_city: z.string().min(1, `required`),
  coApplicant_state: z.enum(STATE_CODES),
  coApplicant_zip: z.string().regex(zipCodePattern),
  coApplicant_employer: z.string().min(1, `required`),
  coApplicant_employmentDuration: z
    .string()
    .regex(/^[0-9]*$/, `must be a number in years`)
    .nullable(),
  coApplicant_applicantSignature: z.string().min(1, `required`),
  coApplicant_phoneNumber: z
    .string()
    .length(11, `must be 11 digits`)
    .regex(/^\d+$/, `Must only contain numbers`),
  coApplicant_phoneType: z.enum(PHONE_VALUES),
  coApplicant_email: z.string().email(),
  coApplicant_annualIncome: z.number().min(1),
  coApplicant_occupation: z.string().min(1),
  coApplicant_employmentStatus: z.enum(EMPLOYMENT_STATUS),
  coApplicant_citizenshipStatus: z.enum(CITIZENSHIP_STATUS),
  coApplicant_homeOwnership: z.enum(OWNERSHIP_VALUE),
  coApplicant_homeOccupancy: z.enum(OCCUPANCY_VALUE),
  coApplicant_checkbox0: z.boolean(),
  coApplicant_checkbox1: z.boolean(),
  coApplicant_checkbox2: z.boolean(),
  coApplicant_checkbox3: z.boolean(),
})

export type GoodleapSecondaryApplicantSchema = z.infer<
  typeof goodleapCoApplicantSchema
>

export const goodleapApplicantSchema = z.object({
  firstName: stringNoNumbersOrSpecialCharacters,
  lastName: stringNoNumbersOrSpecialCharacters,
  dob: dateString,
  ssn: ssn4or9,
  street: z.string().min(1, `required`),
  street2: z.string(),
  city: z.string().min(1, `required`),
  state: z.enum(STATE_CODES),
  zip: z.string().regex(zipCodePattern),
  employer: z.string().min(1, `required`),
  employmentDuration: z
    .string()
    .regex(/^[0-9]*$/, `must be a number in years`)
    .nullable(),
  applicantSignature: z.string().min(1, `required`),
  phoneNumber: z
    .string()
    .length(11, `must be 11 digits`)
    .regex(/^\d+$/, `Must only contain numbers`),
  phoneType: z.enum(PHONE_VALUES),
  email: z.string().email(),
  annualIncome: z.number().min(1),
  occupation: z.string().min(1),
  employmentStatus: z.enum(EMPLOYMENT_STATUS),
  citizenshipStatus: z.enum(CITIZENSHIP_STATUS),
  homeOwnership: z.enum(OWNERSHIP_VALUE),
  homeOccupancy: z.enum(OCCUPANCY_VALUE),
  checkbox0: z.boolean(),
  checkbox1: z.boolean(),
  checkbox2: z.boolean(),
  checkbox3: z.boolean(),
  coApplicant: z.boolean(),
})

export type GoodleapApplicantSchema = z.infer<typeof goodleapApplicantSchema>

export const fullGoodleapSchema = goodleapApplicantSchema.merge(
  goodleapCoApplicantSchema,
)

export type GoodleapForm = z.infer<typeof fullGoodleapSchema>

export const goodleapAmountSchema = z.object({
  type: z.string(),
  value: z.number().nullable(),
})
export const goodleapPaymentSchema = z.object({
  paydownAmount: goodleapAmountSchema.nullable(),
  amount: goodleapAmountSchema,
  roundedAmount: goodleapAmountSchema,
  type: z.string(),
  interval: z.string(),
  paymentDescription: z.string().nullable(),
})

export type GoodleapPayment = z.infer<typeof goodleapPaymentSchema>

export const goodleapPaymentsSchema = z.object({
  calculations: z.array(goodleapPaymentSchema),
})
